/**
 * Query-string values, checked the same way on every route of both servers
 * (`checkRequest('query', …)`, `./requestCheck.ts`). Query values arrive as
 * text, so each helper turns text into the value a handler uses — and refuses
 * text that isn't one, instead of reading `abc` as `NaN` or `1abc` as `1`.
 */
import { z } from 'zod';

/** A non-negative whole number written in digits. */
export const wholeNumber = z
  .string()
  .regex(/^\d+$/, 'must be a whole number')
  .transform((v) => Number(v));

/**
 * A count with bounds, e.g. a page size: below `min` is refused, above `max`
 * is served as `max` (asking for more than a page holds is not an error), and
 * absent is `fallback`.
 */
export function boundedCount(bounds: { min: number; max: number; fallback: number }) {
  return wholeNumber
    .refine((v) => v >= bounds.min, `must be at least ${bounds.min}`)
    .transform((v) => Math.min(v, bounds.max))
    .optional()
    .transform((v) => v ?? bounds.fallback);
}

/** The same as {@link boundedCount} without a default: absent stays absent (the callee has its own). */
export function optionalCount(bounds: { min: number; max: number }) {
  return wholeNumber
    .refine((v) => v >= bounds.min, `must be at least ${bounds.min}`)
    .transform((v) => Math.min(v, bounds.max))
    .optional();
}

/** On/off: `true`/`1` or `false`/`0`; absent is off. */
export const flag = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1')
  .optional()
  .transform((v) => v ?? false);

/** Text; an empty value (`?q=`) is the same as leaving it out. */
export const text = z
  .string()
  .trim()
  .optional()
  .transform((v) => (v ? v : undefined));

/** A point in time JavaScript can read (ISO 8601). */
export const instant = z
  .string()
  .refine((v) => !Number.isNaN(Date.parse(v)), 'must be a date/time (ISO 8601)')
  .optional();
